import React, { useMemo, useState } from 'react';
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import moment from 'moment';
import { formatDay, formatNumber, formatPct, pctColor } from '../utils/monthlyDispatchTrackerFormat';

const CURRENT_LINE_COLOR = '#E72D88';
const BASELINE_LINE_COLOR = '#9e9e9e';
const OTHER_LINE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#6a1b9a', '#00838f', '#5d4037'];
const WORKING_DAY_BAR_COLOR = '#1976d2';
const OVERTIME_COLOR = '#ed6c02';
const MUTED_TEXT = '#919EAB';
const AXIS_CALENDAR = 'calendar';
const AXIS_WORKING = 'working';

const getLineColor = (month, index) => {
    if (month.isCurrent) return CURRENT_LINE_COLOR;
    if (month.isBaseline) return BASELINE_LINE_COLOR;
    return OTHER_LINE_COLORS[index % OTHER_LINE_COLORS.length];
};

const getMonthLegendLabel = (month) => {
    if (month.isCurrent) return `${month.label} (${month.isComplete ? 'full month' : 'to date'})`;
    if (month.isBaseline) return `${month.label} (baseline)`;
    return month.label;
};

const getDayType = (dayEntry) => {
    if (!dayEntry) return null;
    if (dayEntry.isWeekend) return 'Weekend';
    if (dayEntry.isBankHoliday) return 'Bank holiday';
    return null;
};

function DayTypeChip({ dayEntry }) {
    const dayType = getDayType(dayEntry);
    if (!dayType) return null;
    return (
        <Chip
            label={dayType}
            size="small"
            sx={{ height: 18, fontSize: 10, fontWeight: 600, color: OVERTIME_COLOR, backgroundColor: 'rgba(237, 108, 2, 0.1)' }}
        />
    );
}

export function CumulativePaceChart({ months, totals, isLive }) {
    const [axisMode, setAxisMode] = useState(AXIS_CALENDAR);
    const currentMonth = months[months.length - 1];
    const baselineMeta = months[0];
    const dailyByMonth = useMemo(() => totals.dailyByMonth || {}, [totals.dailyByMonth]);
    const isCalendar = axisMode === AXIS_CALENDAR;

    const chartData = useMemo(() => {
        if (isCalendar) {
            return Array.from({ length: 31 }, (_, index) => {
                const point = { x: index + 1 };
                months.forEach((month) => {
                    const series = dailyByMonth[month.key] || [];
                    if (index >= series.length) return;
                    point[month.key] = series.slice(0, index + 1).reduce((sum, entry) => sum + entry.dispatched, 0);
                });
                return point;
            });
        }
        const cumulativeByMonth = totals.cumulativeByMonth || {};
        const maxWorkingDays = Math.max(0, ...months.map((month) => month.totalWorkingDays));
        return Array.from({ length: maxWorkingDays }, (_, index) => {
            const point = { x: index + 1 };
            months.forEach((month) => {
                const entry = (cumulativeByMonth[month.key] || [])[index];
                if (entry) point[month.key] = entry.cumulative;
            });
            return point;
        });
    }, [isCalendar, months, dailyByMonth, totals.cumulativeByMonth]);

    const getDayEntry = (month, x) => (dailyByMonth[month?.key] || [])[x - 1] || null;
    const getDateKey = (month, x) => (isCalendar
        ? getDayEntry(month, x)?.date || null
        : month?.workingDayDates?.[x - 1] || null);

    const asOfX = isCalendar
        ? (dailyByMonth[currentMonth.key] || []).length
        : currentMonth.elapsedWorkingDays;
    const maxX = chartData.length;

    const renderTick = ({ x, y, payload }) => {
        const value = payload.value;
        const isAsOf = value === asOfX;
        const dateKey = getDateKey(currentMonth, value);
        const dayEntry = isCalendar ? getDayEntry(currentMonth, value) : null;
        const isOvertimeDay = Boolean(getDayType(dayEntry));
        const showEveryTick = maxX <= 23 || value % 2 === 1 || isAsOf;
        if (!showEveryTick) return null;
        const subLabel = isCalendar
            ? (dateKey ? moment(dateKey, 'YYYY-MM-DD').format('ddd') : '')
            : (dateKey ? moment(dateKey, 'YYYY-MM-DD').format('D MMM') : '');
        let subColor = MUTED_TEXT;
        if (isOvertimeDay) subColor = OVERTIME_COLOR;
        if (isAsOf) subColor = CURRENT_LINE_COLOR;
        return (
            <g transform={`translate(${x},${y})`}>
                <text dy={14} textAnchor="middle" fontSize={11} fontWeight={isAsOf ? 700 : 500} fill="#1C252E">
                    {isCalendar ? value : `WD ${value}`}
                </text>
                {subLabel && (
                    <text dy={28} textAnchor="middle" fontSize={10} fontWeight={isOvertimeDay || isAsOf ? 700 : 400} fill={subColor}>
                        {subLabel}
                    </text>
                )}
            </g>
        );
    };

    const renderTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const baselineValue = payload.find((item) => item.dataKey === baselineMeta.key)?.value;
        return (
            <Paper elevation={3} sx={{ p: 1.5, minWidth: 280 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {isCalendar ? `Day ${label} of the month` : `Working day ${label}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    Running total from the 1st of each month
                </Typography>
                {[...payload].reverse().map((item) => {
                    const month = months.find((entry) => entry.key === item.dataKey);
                    const dateKey = getDateKey(month, label);
                    const dayEntry = isCalendar ? getDayEntry(month, label) : null;
                    const diffPct = month && !month.isBaseline && Number.isFinite(baselineValue) && baselineValue > 0
                        ? ((item.value - baselineValue) / baselineValue) * 100
                        : null;
                    return (
                        <Box key={item.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontWeight: month?.isCurrent ? 700 : 500, minWidth: 52 }}>
                                {month?.label}
                            </Typography>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {dateKey ? moment(dateKey, 'YYYY-MM-DD').format('ddd D MMM') : ''}
                                </Typography>
                                <DayTypeChip dayEntry={dayEntry} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                {formatNumber(item.value)}
                            </Typography>
                            {Number.isFinite(diffPct) && (
                                <Typography variant="caption" sx={{ color: pctColor(diffPct), fontWeight: 600, minWidth: 48, textAlign: 'right' }}>
                                    {formatPct(diffPct)}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Paper>
        );
    };

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: '1 1 480px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Month-to-date pace: cumulative dispatched
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {isCalendar ? (
                            <>
                                Horizontal axis is the <strong>day of the month</strong> (1st to 31st), including Saturdays, Sundays
                                and bank holidays, so weekend overtime is visible. The weekday under each day is for{' '}
                                {currentMonth.label}; weekends and bank holidays are shown in orange.
                            </>
                        ) : (
                            <>
                                Horizontal axis is the <strong>working day number</strong> (Mon–Fri, excluding UK bank holidays),
                                so months line up fairly. Weekend/bank holiday overtime is added to the next working day. The date
                                under each WD is the {currentMonth.label} date.
                            </>
                        )}
                    </Typography>
                </Box>
                <ToggleButtonGroup size="small" exclusive value={axisMode} onChange={(_, value) => value && setAxisMode(value)}>
                    <ToggleButton value={AXIS_CALENDAR}>Calendar day</ToggleButton>
                    <ToggleButton value={AXIS_WORKING}>Working day</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Box sx={{ width: '100%', height: 360, mt: 1 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" interval={0} height={40} tick={renderTick} tickLine={false} />
                        <YAxis tickFormatter={(value) => value.toLocaleString('en-GB')} width={64} />
                        <Tooltip content={renderTooltip} />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            height={32}
                            formatter={(key) => {
                                const month = months.find((entry) => entry.key === key);
                                return month ? getMonthLegendLabel(month) : key;
                            }}
                        />
                        {!currentMonth.isComplete && asOfX > 0 && (
                            <ReferenceLine
                                x={asOfX}
                                stroke={CURRENT_LINE_COLOR}
                                strokeDasharray="2 4"
                                label={{
                                    value: isLive ? 'Today' : 'As at',
                                    position: 'insideTopRight',
                                    fill: CURRENT_LINE_COLOR,
                                    fontSize: 11,
                                    fontWeight: 700,
                                }}
                            />
                        )}
                        {months.map((month, index) => (
                            <Line
                                key={month.key}
                                type="monotone"
                                dataKey={month.key}
                                stroke={getLineColor(month, index)}
                                strokeWidth={month.isCurrent ? 3 : 2}
                                strokeDasharray={month.isBaseline && !month.isCurrent ? '6 3' : undefined}
                                dot={false}
                                activeDot={{ r: 4 }}
                                connectNulls={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}

export function DailyDispatchChart({ months, totals }) {
    const currentMonth = months[months.length - 1];
    const [selectedKey, setSelectedKey] = useState(currentMonth.key);
    const monthKey = months.some((month) => month.key === selectedKey) ? selectedKey : currentMonth.key;
    const selectedMonth = months.find((month) => month.key === monthKey);
    const series = useMemo(() => (totals.dailyByMonth || {})[monthKey] || [], [totals.dailyByMonth, monthKey]);

    const overtime = useMemo(() => {
        const overtimeDays = series.filter((entry) => !entry.isWorkingDay && entry.dispatched > 0);
        return {
            days: overtimeDays.length,
            orders: overtimeDays.reduce((sum, entry) => sum + entry.dispatched, 0),
            dates: overtimeDays.map((entry) => `${moment(entry.date, 'YYYY-MM-DD').format('ddd D MMM')} (${formatNumber(entry.dispatched)})`),
        };
    }, [series]);

    const renderTick = ({ x, y, payload }) => {
        const entry = series[payload.value - 1];
        const isOvertimeDay = Boolean(getDayType(entry));
        return (
            <g transform={`translate(${x},${y})`}>
                <text dy={14} textAnchor="middle" fontSize={11} fill="#1C252E">
                    {payload.value}
                </text>
                {entry && (
                    <text dy={28} textAnchor="middle" fontSize={10} fontWeight={isOvertimeDay ? 700 : 400} fill={isOvertimeDay ? OVERTIME_COLOR : MUTED_TEXT}>
                        {moment(entry.date, 'YYYY-MM-DD').format('dd')}
                    </text>
                )}
            </g>
        );
    };

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const entry = payload[0].payload;
        return (
            <Paper elevation={3} sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {moment(entry.date, 'YYYY-MM-DD').format('dddd D MMM YYYY')}
                    </Typography>
                    <DayTypeChip dayEntry={entry} />
                </Box>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    Dispatched: <strong>{formatNumber(entry.dispatched)}</strong>
                </Typography>
            </Paper>
        );
    };

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: '1 1 480px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Daily dispatched: {selectedMonth?.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Every calendar day, all clients. Blue = working day; orange = Saturday, Sunday or bank holiday (overtime).
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: overtime.orders > 0 ? OVERTIME_COLOR : 'text.secondary', fontWeight: 600 }}>
                        {overtime.orders > 0
                            ? `Weekend / bank holiday overtime: ${formatNumber(overtime.orders)} orders over ${overtime.days} day${overtime.days === 1 ? '' : 's'}: ${overtime.dates.join(', ')}`
                            : 'No weekend or bank holiday dispatches this month.'}
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="daily-dispatch-month-label">Month</InputLabel>
                    <Select
                        labelId="daily-dispatch-month-label"
                        label="Month"
                        value={monthKey}
                        onChange={(event) => setSelectedKey(event.target.value)}
                    >
                        {[...months].reverse().map((month) => (
                            <MenuItem key={month.key} value={month.key}>
                                {month.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ width: '100%', height: 280, mt: 1 }}>
                <ResponsiveContainer>
                    <BarChart data={series} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="day" interval={0} height={40} tick={renderTick} tickLine={false} />
                        <YAxis tickFormatter={(value) => value.toLocaleString('en-GB')} width={56} />
                        <Tooltip content={renderTooltip} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
                        <Bar dataKey="dispatched" radius={[3, 3, 0, 0]}>
                            {series.map((entry) => (
                                <Cell key={entry.date} fill={entry.isWorkingDay ? WORKING_DAY_BAR_COLOR : OVERTIME_COLOR} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            {selectedMonth?.isCurrent && !selectedMonth.isComplete && (
                <Typography variant="caption" color="text.secondary">
                    Month to date, up to {formatDay(selectedMonth.periodEndDate)}.
                </Typography>
            )}
        </Paper>
    );
}
